import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { buildApiUrl } from "../../apiConfig";
import { getOnlineAdditionalActReadiness } from "./onlineAdditionalActReadiness";
import { parseRonCents, validatePriceReduction } from "./priceReduction";

const endpoint = "/api/contracte/zoho/act-aditional-comun-acord";
const requestId = (value) => String(value || "").trim();
const unknownMessage = "Rezultatul trimiterii nu a putut fi confirmat. Verifică documentul în Zoho înainte de o nouă încercare. Trimiterea și modificarea sumei sunt blocate în acest panou.";

const storedOperation = (data, issueId) => {
  const workflow = data?.lp015Workflow;
  if (!workflow || workflow.blocksNew === false) return null;
  const id = requestId(workflow.request_id);
  const validTerms = validatePriceReduction(workflow.currentPrice, workflow.newPrice).isValid;
  const terms = validTerms ? {
    issueId,
    newPrice: (parseRonCents(workflow.newPrice) / 100).toFixed(2),
    expectedCurrentPrice: workflow.currentPrice,
  } : null;
  if (workflow.submitted === true && id) {
    return { status: "sent", terms, requestId: id, severity: "success",
      message: workflow.message || "Actul adițional LP015 a fost deja trimis prin Zoho. O nouă trimitere este blocată cât timp acest act este în curs." };
  }
  if (workflow.recoverable === true && workflow.retryable !== false && id && terms) {
    return { status: "recoverable", terms, requestId: id, severity: "warning",
      message: workflow.message || "Există un document LP015 în Zoho. Reia trimiterea aceluiași act, cu suma deja stabilită." };
  }
  return { status: "blocked", terms, requestId: id, severity: "warning",
    message: workflow.message || (workflow.status === "unavailable"
      ? "Actele adiționale existente nu au putut fi verificate. Reîncarcă panoul înainte de trimitere."
      : "Există deja un act adițional LP015 în curs sau cu rezultat neconfirmat. Verifică documentul în Zoho înainte de o nouă trimitere.") };
};

const responseOperation = (result, terms, dataSnapshot, knownRequestId = "") => {
  const returnedId = requestId(result?.request_id);
  // A resume failure can happen before the server looks up the existing draft.
  // Keep its identity and terms even when that response contains no request ID.
  const id = returnedId || knownRequestId;
  if (result?.ok === true && result?.submitted === true && returnedId) {
    return { status: "sent", terms, requestId: id, severity: "success",
      message: result.message || "Actul adițional LP015 a fost creat și trimis la semnat prin Zoho." };
  }
  const canRetryKnownDraft = Boolean(knownRequestId && !returnedId && result?.retryable === true);
  if (result?.ok === false && id && (
    (result.recoverable === true && result.retryable !== false) || canRetryKnownDraft
  )) {
    return { status: "recoverable", terms, requestId: id, severity: "warning",
      message: `${result.message || "Trimiterea actului adițional nu a fost finalizată."} Reîncearcă trimiterea aceluiași document din Zoho; suma rămâne neschimbată.` };
  }
  if (result?.ok === false && result.retryable === true && !id) {
    return { status: result.code === "STALE_CURRENT_PRICE" ? "stale" : "idle", severity: "error", dataSnapshot,
      message: result.message || "Datele nu au putut fi validate. Verifică informațiile și reîncearcă." };
  }
  return { status: "blocked", terms, requestId: id, severity: "error",
    message: result?.message ? `${result.message} Trimiterea și modificarea sumei sunt blocate în acest panou.` : unknownMessage };
};

export default function useOnlineAdditionalAct({ issueId, data, newPrice }) {
  const context = useMemo(() => ({ issueId, active: true, inFlight: false, operation: null }), [issueId]);
  const currentContext = useRef(context);
  currentContext.current = context;
  const [state, setState] = useState(null);

  useEffect(() => {
    context.active = true;
    return () => { context.active = false; };
  }, [context]);

  const previous = state?.context === context ? state.operation : null;
  const operation = previous?.status === "stale" && previous.dataSnapshot !== data
    ? storedOperation(data, issueId)
    : previous || storedOperation(data, issueId);
  const status = operation?.status || "idle";
  const locked = ["sending", "recoverable", "blocked", "sent"].includes(status);
  const effectivePrice = locked && operation?.terms ? operation.terms.newPrice : newPrice;
  const readiness = getOnlineAdditionalActReadiness({ data, newPrice: effectivePrice });
  const canSubmit = Boolean(
    data?.issueId === issueId && readiness.isReady &&
    (status === "idle" || status === "recoverable"),
  );

  const create = async () => {
    if (!context.active || context.inFlight || !canSubmit) return;
    const latest = context.operation;
    if (latest && ["sending", "blocked", "sent"].includes(latest.status)) return;
    if (latest?.status === "recoverable" && status !== "recoverable") return;
    const knownRequestId = status === "recoverable" ? operation.requestId : "";
    const terms = status === "recoverable" ? operation.terms : {
      issueId,
      newPrice: (parseRonCents(newPrice) / 100).toFixed(2),
      expectedCurrentPrice: data.currentPrice,
    };
    const publish = (next) => {
      if (!context.active || currentContext.current !== context) return;
      context.operation = next;
      setState({ context, operation: next });
    };
    context.inFlight = true;
    publish({ status: "sending", terms, requestId: knownRequestId, severity: "info", message: "Se creează și se trimite actul adițional prin Zoho…" });
    try {
      const response = await axios.post(buildApiUrl(endpoint), {
        ...terms,
        ...(knownRequestId ? { existing_request_id: knownRequestId } : {}),
      });
      publish(responseOperation(response.data, terms, data, knownRequestId));
    } catch (error) {
      publish(responseOperation(error.response?.data, terms, data, knownRequestId));
    } finally {
      context.inFlight = false;
    }
  };

  return {
    create,
    canSubmit,
    locked,
    creating: status === "sending",
    recoverable: status === "recoverable",
    sent: status === "sent",
    newPrice: effectivePrice,
    feedback: operation?.message ? operation : null,
  };
}
